import React from 'react';
import { Send } from 'lucide-react';
import { CompactError, Spinner, Tooltip } from '../../ui';

/**
 * The buyer's box under the open conversation. A failed attempt is reported
 * on its own bubble; the error here is only for a send that never left the
 * browser (no RFQ, no address), which has no bubble to carry it.
 */
interface ThreadComposerProps {
  supplierName: string;
  hasContactEmail: boolean;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** A send is in flight on this thread. */
  isSending: boolean;
  sendError: string | null;
  onDismissSendError: () => void;
  composerRef: React.RefObject<HTMLTextAreaElement | null>;
}

const ThreadComposer: React.FC<ThreadComposerProps> = ({
  supplierName,
  hasContactEmail,
  value,
  onChange,
  onSend,
  isSending,
  sendError,
  onDismissSendError,
  composerRef,
}) => (
  <div className="shrink-0 border-t border-line-divider-soft px-[14px] py-s3">
    {sendError && (
      <CompactError
        layout="row"
        className="mb-s3"
        title="I didn’t send that"
        message={sendError}
        action={{ label: 'Dismiss', onClick: onDismissSendError }}
      />
    )}
    <div className="flex items-end gap-s2 rounded-[9px] border border-line bg-canvas px-[11px] py-s3">
      <textarea
        ref={composerRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSend();
        }}
        placeholder={`Tell me what to ask ${supplierName} — I turn it into the email and send it`}
        rows={2}
        className="w-full flex-1 resize-none border-none bg-transparent font-sans text-[12.5px] leading-[1.5] text-ink outline-none placeholder:text-ink-muted"
      />
      {/* The explainer lives on the control it is about rather than costing the
          conversation a row of sub-text on every render. */}
      <Tooltip
        side="top"
        content={
          !hasContactEmail
            ? 'Add a contact email above to enable sending'
            : `I turn this into the email and send it to ${supplierName} (⌘⏎) — your words never go to them directly.`
        }
      >
        <button
          type="button"
          onClick={onSend}
          disabled={!value.trim() || isSending || !hasContactEmail}
          aria-label="Send this ask"
          aria-busy={isSending || undefined}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control text-action hover:bg-action-bg disabled:pointer-events-none disabled:opacity-40"
        >
          {isSending ? <Spinner /> : <Send className="h-4 w-4" />}
        </button>
      </Tooltip>
    </div>
  </div>
);

export default ThreadComposer;
